import {
  KnownCreateDeliveryDtoChannel,
  KnownDeliveryContainerDtoType,
  KnownHandofftoKioskResponseDtoMessage,
  KnownCreateDeliveryDtoItemType,
  KnownHubDetailsDtoStatus,
  KnownOrderDtoStatus,
  OrderDto,
  KnownHubDetailsDtoType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import * as _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useMutationDeliverViaCloud,
  useMutationHandOffDeliverViaCloud,
  useMutationRetrieveViaCloud,
  useQueryGetOrderTimeline
} from '../../order-details.queries';
import { useQueryListHubsDetails } from '../../../../lockers/hubs.queries';
import { HubDetails } from '../../../../lockers/models/hub-details.model';
import { useForm } from 'react-hook-form';
import { useMutationCreateDelivery } from '../../../../deliveries/deliveries.queries';
import { v4 as uuidv4 } from 'uuid';
import { naturalSort } from '../../../../../core/util/natural-sort.util';
import { useQueryGetOrder } from '../../../orders.queries';

export interface DeliveryActionProps {
  order: OrderDto;
  onAbort(): void;
  onComplete(): void;
}

interface DeliveryActionState {
  disableAutoClose?: boolean;
  selectedHubType: KnownHubDetailsDtoType | null;
  avaialbleActions: {
    deliverViaCloud: boolean;
    deliverViaQrCode: boolean;
  };
}

interface ConfirmationStepState extends DeliveryActionState {
  current: 'CONFIRMATION_STEP';
}

interface SendingRequestToKioskState extends DeliveryActionState {
  current: 'SENDING_DELIVERY_REQUEST';
}

interface ConnectingToKioskState extends DeliveryActionState {
  current: 'CONNECTING_TO_KIOSK';
}

interface HandoverCompleteState extends DeliveryActionState {
  current: 'HANDOVER_COMPLETE';
}

interface DeliveryRequestCompleteState extends DeliveryActionState {
  current: 'DELIVERY_COMPLETE';
}

interface FailedToConnectState extends DeliveryActionState {
  current: 'FAILED_TO_CONNECT';
  error: {
    title: string;
    details: string;
  };
}

interface SkippedToScanState extends DeliveryActionState {
  current: 'SKIPPED_TO_SCAN';
}

interface ErrorState extends DeliveryActionState {
  current: 'ERROR';
  error: {
    title: string;
    details: string;
  };
}

type DeliveryActionStates =
  | ConfirmationStepState
  | ConnectingToKioskState
  | SendingRequestToKioskState
  | HandoverCompleteState
  | DeliveryRequestCompleteState
  | FailedToConnectState
  | SkippedToScanState
  | ErrorState;

const initialState: ConfirmationStepState = {
  disableAutoClose: true,
  current: 'CONFIRMATION_STEP',
  selectedHubType: null,
  avaialbleActions: {
    deliverViaCloud: false,
    deliverViaQrCode: false
  }
};

export interface DeliveryActionHandlers {
  onSelectHub(hubID: string): void;
  onSelectDeliveryContainer(deliveryContainerID: string): void;
  onDeliverIntoSpacesHub(): void;
  onQrCodeHandoff(): void;
  onDeliverIntoKiosklessSpacesHub(): void;
  onSkipToQrScan(): void;
  onClose(): void;
  onRestart(): void;
  onComplete(): void;
}

export interface DeliverNowModalForm {
  hubID?: string;
  deliveryContainerID?: string;
}

export function useDeliveryAction({ order, onAbort, onComplete }: DeliveryActionProps) {
  const [state, setState] = useState<DeliveryActionStates>(initialState);

  // We need to keep track of the order status when the hook is first created
  const [orderStatus] = useState(order.status);

  // This query subscribes to live updates for the order so we can react to status changes
  const liveOrderData = useQueryGetOrder(order.retailer.id, order.id);

  useEffect(() => {
    const liveStatus = liveOrderData.data?.status;
    if (liveStatus !== undefined && liveStatus !== orderStatus) {
      // If the order's status has changed since the modal was opened (e.g. due to a Kiosk event or any
      // other external factor) then we assume the action has been completed elsewhere and close the modal.
      // This ensures the modal doesn't get left open regardless of which status the order transitions to.
      onAbort();
    }
  }, [liveOrderData.data?.status, orderStatus, onAbort]);

  const timelineData = useQueryGetOrderTimeline(order.retailer.id, order.id);
  const hubsData = useQueryListHubsDetails(order.retailer.id);

  const isLoading = timelineData.isLoading || hubsData.isLoading;

  const qrCodeHandoffMutation = useMutationHandOffDeliverViaCloud(order.retailer.id, order.id);
  const deliverIntoSpacesHubMutation = useMutationDeliverViaCloud(order.retailer.id, order.id);
  const retrieveViaCloudMutation = useMutationRetrieveViaCloud(order.retailer.id, order.id);
  const deliverIntoKiosklessSpacesHubMutation = useMutationCreateDelivery();

  const handoffToKioskMutation =
    orderStatus === KnownOrderDtoStatus.Booked ? qrCodeHandoffMutation : retrieveViaCloudMutation;

  const form = useForm<DeliverNowModalForm>({
    defaultValues: {
      hubID: '',
      deliveryContainerID: ''
    }
  });

  const hubID = form.watch('hubID');
  const deliveryContainerID = form.watch('deliveryContainerID');

  // Determine available actions based on selected hub
  const selectedHub = useMemo(() => (hubsData.data ?? []).find((hub) => hub.id === hubID), [hubsData.data, hubID]);
  useEffect(() => {
    let canDeliverViaQrCode = false;
    let canDeliverViaCloud = false;
    let selectedHubType: KnownHubDetailsDtoType | null = null;

    if (selectedHub) {
      selectedHubType = selectedHub.type;
      switch (selectedHub.type) {
        case KnownHubDetailsDtoType.SpacesHub:
        case KnownHubDetailsDtoType.LockersHub:
          canDeliverViaCloud = true;
          canDeliverViaQrCode = true;
          break;
        case KnownHubDetailsDtoType.KiosklessSpacesHub:
          canDeliverViaCloud = true;
          break;
        case KnownHubDetailsDtoType.LockersHubExternal:
          canDeliverViaQrCode = true;
          break;
      }
    } else {
      selectedHubType = null;
    }

    // Only update the state if something has changed to avoid re-renders
    if (
      canDeliverViaCloud !== state.avaialbleActions.deliverViaCloud ||
      canDeliverViaQrCode !== state.avaialbleActions.deliverViaQrCode
    ) {
      setState((state) => ({
        ...state,
        selectedHubType,
        avaialbleActions: {
          deliverViaCloud: canDeliverViaCloud,
          deliverViaQrCode: canDeliverViaQrCode
        }
      }));
    }
  }, [selectedHub, state.avaialbleActions.deliverViaCloud, state.avaialbleActions.deliverViaQrCode]);

  // Close the modal when the order's "current action" clears while the modal is open. The order status
  // doesn't always change when something happens at the Kiosk (e.g. a delivery cancelled at the Kiosk leaves
  // the order Booked), so status alone isn't enough. The timeline's `currentAction` represents the live
  // action pending on the order (awaiting handoff/collection/etc.); when it disappears, that action has been
  // resolved one way or another - delivered, collected, cancelled at the Kiosk - and the modal is out of
  // date, so we close it. `useQueryGetOrderTimeline` keeps the timeline live via its own subscription.
  //
  // We edge-trigger on the transition from "has a current action" to "no current action" so that (a) opening
  // the modal on an order that has no current action doesn't close it immediately and (b) the current action
  // being (re)populated as the user drives the handoff doesn't trigger a close.
  const hasCurrentAction = timelineData.data?.currentAction != null;
  const hadCurrentActionRef = useRef(false);
  useEffect(() => {
    if (!timelineData.data) {
      return;
    }
    if (hasCurrentAction) {
      hadCurrentActionRef.current = true;
      return;
    }
    if (hadCurrentActionRef.current) {
      onAbort();
    }
  }, [timelineData.data, hasCurrentAction, onAbort]);

  const hubSelectOptions = useMemo(() => {
    const options = (hubsData.data ?? [])
      .filter((hub) => hub.status === KnownHubDetailsDtoStatus.Active)
      .map((hub) => ({
        label: hub.name,
        value: hub.id,
        type: hub.type
      }));
    return naturalSort(options).asc((option) => option.label);
  }, [hubsData.data]);

  useEffect(() => {
    // If the hub is already selected then don't change it
    if (hubID) return;

    let availableHubs = hubSelectOptions;
    if (orderStatus === KnownOrderDtoStatus.RetrievalRequired) {
      availableHubs = availableHubs.filter((k) => k.value === order.hub?.id);
    }
    // If the order has been booked into a specific hub then select it.
    if (order.bookedIntoHub?.id) {
      const hub = availableHubs.find((h) => h.value === order.bookedIntoHub?.id);

      if (hub) {
        form.setValue('hubID', hub.value);
      }
    }

    const onlyAvailableHubID = availableHubs.length === 1 ? availableHubs[0].value : undefined;
    if (onlyAvailableHubID) {
      form.setValue('hubID', onlyAvailableHubID);
    }
  }, [form, hubSelectOptions, hubsData.data, order.bookedIntoHub, order.hub?.id, orderStatus, hubID]);

  const hubContainerSelectOptions = useMemo(() => {
    if (!hubID) return [];
    const selectedHub = (hubsData.data ?? []).find((hub) => hub.id === hubID);
    if (!selectedHub) return [];
    return getHubContainerSelectOptions(selectedHub);
  }, [hubsData.data, hubID]);

  const getSelectedHub = () => {
    return (hubsData.data ?? []).find((hub) => hub.id === hubID);
  };

  const getSelectedDeliveryContainer = () => {
    if (!hubID) return undefined;
    if (!deliveryContainerID) return undefined;
    const selectedHub = getSelectedHub();
    if (!selectedHub) return undefined;

    const allContainers = getDeliveryContainers(selectedHub);
    const container = allContainers.find((container) => container.id === deliveryContainerID);
    return container;
  };

  const onDeliverIntoSpacesHub = () => {
    const selectedHub = getSelectedHub();
    const selectedDeliveryContainer = getSelectedDeliveryContainer();

    if (!selectedHub || !selectedDeliveryContainer) {
      setState((state) => ({
        ...state,
        current: 'FAILED_TO_CONNECT',
        disableAutoClose: true,
        error: {
          title: 'There was a problem identifying the selected hub',
          details: 'Please close the modal and try again'
        }
      }));
      return;
    }
    setState((state) => ({
      ...state,
      disableAutoClose: true, // We don't want this dialog to close until the user has finished
      current: 'SENDING_DELIVERY_REQUEST'
    }));

    let nextState: DeliveryActionStates;

    const done = _.after(2, () => {
      if (nextState !== undefined) {
        setState((state) => ({ ...state, ...nextState }));
      }
    });

    setTimeout(() => done(), 1500);

    deliverIntoSpacesHubMutation.mutate(
      {
        hubID: selectedHub.id,
        deliveryContainerID: selectedDeliveryContainer.id
      },
      {
        onSuccess(response) {
          if (!response.method) {
            nextState = {
              ...state,
              current: 'FAILED_TO_CONNECT',
              disableAutoClose: true,
              error: {
                title: 'Unable to send delivery request',
                details: 'Please close the modal and try again'
              }
            };
          } else {
            nextState = {
              ...state,
              disableAutoClose: true,
              current: 'DELIVERY_COMPLETE'
            };
          }
          done();
        },
        onError() {
          nextState = {
            ...state,
            current: 'ERROR',
            disableAutoClose: true,
            error: {
              title: 'Unable to send delivery request',
              details: 'Please close the modal and try again'
            }
          };
          done();
        }
      }
    );
  };

  const onQrCodeHandoff = () => {
    const selectedHub = getSelectedHub();
    if (!selectedHub) {
      setState((state) => ({
        ...state,
        current: 'FAILED_TO_CONNECT',
        error: {
          title: 'There was a problem identifying the selected hub',
          details: 'Please close the modal and try again'
        }
      }));
      return;
    }
    setState((state) => ({
      ...state,
      current: 'CONNECTING_TO_KIOSK',
      // We will re-enable the auto close after the handover is complete
      disableAutoClose: true
    }));
    let setNextState: (state: DeliveryActionStates) => DeliveryActionStates;

    const done = _.after(2, () => {
      if (setNextState !== undefined) {
        setState((state) => ({ ...state, ...setNextState }));
      }
    });

    setTimeout(() => done(), 1500);

    handoffToKioskMutation.mutate(selectedHub.kiosk!.id, {
      onSuccess(response) {
        if (response.message === KnownHandofftoKioskResponseDtoMessage.Success) {
          setNextState = (state) => ({
            ...state,
            current: 'HANDOVER_COMPLETE',
            disableAutoClose: false
          });
        } else if (response.message === KnownHandofftoKioskResponseDtoMessage.KioskBusy) {
          setNextState = (state) => ({
            ...state,
            current: 'FAILED_TO_CONNECT',
            error: {
              title: 'Kiosk currently in use',
              details: 'There is already an active session on the kiosk. Please wait until it has finished.'
            }
          });
        } else if (response.message === KnownHandofftoKioskResponseDtoMessage.ParseError) {
          setNextState = (state) => ({
            ...state,
            current: 'FAILED_TO_CONNECT',
            error: {
              title: 'Unable to start the delivery',
              details: 'The kiosk was unable to handle the request, please try again.'
            }
          });
        } else {
          setNextState = (state) => ({
            ...state,
            current: 'FAILED_TO_CONNECT',
            error: {
              title: 'Unable to start the delivery',
              details: 'An unknown error occurred on the kiosk, please try again.'
            }
          });
        }
        done();
      },
      onError(error) {
        let details = 'The kiosk may be temporarily offline';
        if ((error as any)?.response?.data?.message) {
          details = (error as any).response.data.message;
        }
        setNextState = (state) => ({
          ...state,
          current: 'FAILED_TO_CONNECT',
          error: {
            title: 'Unable to start the delivery',
            details
          }
        });
        done();
      }
    });
  };

  const onDeliverIntoKiosklessSpacesHub = () => {
    if (!hubID || !deliveryContainerID) {
      setState((state) => ({
        ...state,
        current: 'FAILED_TO_CONNECT',
        disableAutoClose: true,
        error: {
          title: 'There was a problem identifying the selected hub',
          details: 'Please close the modal and try again'
        }
      }));
      return;
    }

    setState((state) => ({
      ...state,
      disableAutoClose: true, // We don't want this dialog to close until the user has finished
      current: 'SENDING_DELIVERY_REQUEST'
    }));

    deliverIntoKiosklessSpacesHubMutation.mutate(
      {
        channel: KnownCreateDeliveryDtoChannel.Cloud,
        deliveredByID: order.retailer.id,
        deliveredToID: order.customer?.id,
        deliveryContainerID,
        deliveryRequestID: uuidv4(),
        itemType: KnownCreateDeliveryDtoItemType.RetailOrder,
        projectID: order.retailer.id,
        hubID,
        workflowID: order.id
      },
      {
        onSuccess() {
          setState((state) => ({
            ...state,
            disableAutoClose: true,
            current: 'DELIVERY_COMPLETE'
          }));
        },
        onError() {
          setState((state) => ({
            ...state,
            disableAutoClose: true,
            current: 'ERROR',
            error: {
              title: 'Unable to start the delivery',
              details: 'An unknown error occurred, please try again.'
            }
          }));
        }
      }
    );
  };

  const handlers: DeliveryActionHandlers = {
    onClose() {
      onAbort();
    },
    onRestart() {
      // Go back to the confirmation step
      // but keep the selected hub (and container if applicable)
      // Clear the latched code so a fresh attempt re-derives it from the timeline.
      setLatchedQrCode(undefined);
      setState((state) => ({
        ...state,
        current: 'CONFIRMATION_STEP',
        disableAutoClose: true
      }));
    },
    onSelectHub(hubID) {
      setState((state) => ({
        ...state,
        selectedHubID: hubID
      }));
    },
    onSelectDeliveryContainer(deliveryContainerID) {
      setState((state) => ({
        ...state,
        selectedDeliveryContainerID: deliveryContainerID
      }));
    },
    onSkipToQrScan() {
      setState((state) => ({
        ...state,
        current: 'SKIPPED_TO_SCAN'
      }));
    },
    onDeliverIntoSpacesHub,
    onQrCodeHandoff,
    onDeliverIntoKiosklessSpacesHub,
    onComplete() {
      onComplete();
    }
  };

  const qrCodeContent = useMemo(() => {
    if (!state.selectedHubType) return undefined;
    if (state.selectedHubType === KnownHubDetailsDtoType.LockersHubExternal) {
      return timelineData.data?.currentAction?.details?.barcodeContent;
    }
    return timelineData.data?.currentAction?.details?.qrcodeContent;
  }, [
    state.selectedHubType,
    timelineData.data?.currentAction?.details?.barcodeContent,
    timelineData.data?.currentAction?.details?.qrcodeContent
  ]);

  // The QR/barcode is derived from the live timeline's currentAction. We need to latch on to it
  // in order to display it, regardless of the timeline updates that may occur in the background.
  const [latchedQrCode, setLatchedQrCode] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (qrCodeContent !== undefined) {
      setLatchedQrCode(qrCodeContent);
    }
  }, [qrCodeContent]);

  // When the project is not configured for external locker deliveries, the timeline
  // omits barcodeContent and provides a warning explaining why. The warning only
  // applies to the external lockers barcode, so only surface it for external hubs.
  const qrCodeWarning = useMemo(() => {
    if (state.selectedHubType !== KnownHubDetailsDtoType.LockersHubExternal) return undefined;
    if (timelineData.data?.currentAction?.details?.barcodeContent) return undefined;
    return timelineData.data?.currentAction?.details?.barcodeContentWarning;
  }, [
    state.selectedHubType,
    timelineData.data?.currentAction?.details?.barcodeContent,
    timelineData.data?.currentAction?.details?.barcodeContentWarning
  ]);

  return {
    state,
    selectedHub: getSelectedHub(),
    selectedDeliveryContainer: getSelectedDeliveryContainer(),
    hubSelectOptions,
    hubContainerSelectOptions,
    qrCode: qrCodeContent ?? latchedQrCode,
    qrCodeWarning,
    handlers,
    isLoading,
    form
  } as const;
}

interface DeliveryContainer {
  id: string;
  name: string;
  type: KnownDeliveryContainerDtoType;
}

const getHubContainerSelectOptions = (hub: HubDetails) => {
  const deliveryContainers = getDeliveryContainers(hub).map((container) => ({
    label: container.name,
    value: container.id
  }));

  return naturalSort(deliveryContainers).asc((container) => container.label);
};

const getDeliveryContainers = (hub: HubDetails): DeliveryContainer[] => {
  // Return lockers(from towers) and spaces(from sections) (flatMapped)
  const allLockers =
    hub.towers?.flatMap((tower) =>
      tower.lockers
        .filter((locker) => !locker.isKiosk)
        .map((locker) => ({
          name: `${tower.name} - ${locker.name}`,
          id: locker.id,
          type: KnownDeliveryContainerDtoType.Locker
        }))
    ) ?? [];
  const allSpaces =
    hub.sections?.flatMap((section) =>
      section.spaces
        .filter((space) => !space.isKiosk)
        .map((space) => ({
          name: `${section.name} - ${space.name}`,
          id: space.id,
          type: KnownDeliveryContainerDtoType.Space
        }))
    ) ?? [];

  return naturalSort([...allLockers, ...allSpaces]).asc((i) => i.name);
};
